import ResourceManager from '@/Management/ResourceManager';
import OverlayViewModel from '@/ViewModels/Controls/OverlayViewModel';
import ComponentManager from '@/Management/ComponentManager';
import { ISearchGridDTO } from "coreframetypings/dto";
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';

export default class SearchDescriptionViewModel extends OverlayViewModel {

    public readonly maxEntriesToShow: number;
    public readonly maxWordsToDisplay: number;
    public readonly maxCharactersToDisplay: number;
    public readonly moreEntriesFoundText: string;
    public readonly descriptionHeader: string;
    public readonly valueHeader: string;

    public static readonly DOTS: string = '[...]';
    public searchKeyword: KnockoutObservable<string>;
    public hovered: KnockoutObservable<ISearchGridDTO>;

    public constructor(params: any) {
        super(params);
        this.searchKeyword = params.searchKeyword;
        this.hovered = params.hovered;
        this.maxEntriesToShow = params.maxEntriesToShow ?? 5;
        this.maxWordsToDisplay = params.maxWordsToDisplay ?? 500;
        this.maxCharactersToDisplay = params.maxCharactersToDisplay ?? 750;
        this.moreEntriesFoundText = ResourceManager.getInstance()
            .get('SEARCHDESCRIPTION_MORE_ENTRIES_FOUND');
        this.descriptionHeader = ResourceManager.getInstance()
            .get('SEARCH_TOOLTIP_DESCRIPTION');
        this.valueHeader = ResourceManager.getInstance()
            .get('SEARCH_TOOLTIP_VALUE');
    }

    public buildMinimizedContent(fullContent: Array<string>, value?: string): Array<string> {
        // die Range: Wieviele Woerter Vor/Nach sollen gezeigt werden um einen groben Kontext zu bekommen
        const rangeValue = 30;

        // an welchen Positionen steht unser Suchbegriff?
        const indexes = this.fetchSearchResultIndexes(value, fullContent);
        const resultCollection = [];

        if (indexes.length > 0) {
            // speichern der ersten Indexposition als "lastIndex" -> dient spaeter dazu Duplikationen zu vermeiden
            let lastIndex = indexes[0];

            // bereite den ersten Treffer auf
            resultCollection.push(this.getRelevantContentPiece(lastIndex, rangeValue, fullContent, value));

            lastIndex += rangeValue;

            // bearbeite alle verbleibenden Treffer
            for (let i = 1; i < indexes.length; i++) {
                const current = indexes[i];

                // Berechne auf Basis des aktuellen Index + der Range - letzter Index einen Pruefwert -> Duplikatsfindung
                // -> Wenn innerhalb von z.B. 10 Woertern der Suchbegriff 2x vorkommt, so wuerde diese Stelle 2x gelistet werden - macht nicht viel Sinn, da der Inhalt bereits durch das erste Finding abgedeckt wird
                const check = current + rangeValue - lastIndex;

                // Ist der berechnete Wert groesser als unsere Range - dann wurde dieser Wert noch nicht abgedeckt
                if (check > rangeValue)
                    resultCollection.push(this.getRelevantContentPiece(lastIndex, rangeValue, fullContent, value));

                lastIndex = current + rangeValue;
            }
        }

        return resultCollection;
    }

    private fetchSearchResultIndexes(searchTerm: string, searchField: string[]): Array<number> {
        const indexes: Array<number> = [];
        const regex = new RegExp(`${searchTerm}`, 'ig');
        for (let i = 0; i < searchField.length; i++) {
            if (searchField[i].match(regex))
                indexes.push(i);
        }

        return indexes;
    }

    private getRelevantContentPiece(lastIndex: number, definedRange: number, content: Array<string>, searchTerm?: string): string {
        const endPosition = (lastIndex + definedRange) > content.length ? content.length : lastIndex + definedRange;

        if (lastIndex < definedRange) {
            return this.buildContentValue(content, 0, endPosition, searchTerm);
        } else {
            let startPosition = lastIndex - definedRange;
            return this.buildContentValue(content, startPosition, endPosition, searchTerm);
        }
    }

    public buildContentValue(content: Array<string>, beginIndex: number, endIndex: number, searchTerm?: string): string {
        let returnValue = content.slice(beginIndex, endIndex).join(' ');

        if (returnValue.length > this.maxCharactersToDisplay) {
            if (!searchTerm)
                return returnValue.substr(0, this.maxCharactersToDisplay);

            const indexOfTerm = returnValue.indexOf(searchTerm);
            // nimm wenn moeglich 30 Zeichen vor dem Suchtreffer als beginnenden Index -> wenn negativ-Wert, nimm 0
            const calculatedIndex = indexOfTerm - 30;
            const beginIndex = calculatedIndex < 0 ? 0 : calculatedIndex;

            return (beginIndex != 0 ? `${SearchDescriptionViewModel.DOTS} ` : '') + returnValue.substr(beginIndex < 0 ? 0 : beginIndex, this.maxCharactersToDisplay) + ` ${SearchDescriptionViewModel.DOTS}`;
        }

        if (beginIndex != 0)
            returnValue = `${SearchDescriptionViewModel.DOTS} ` + returnValue;

        if (endIndex != content.length)
            returnValue = returnValue + ` ${SearchDescriptionViewModel.DOTS}`;

        return returnValue;
    }

    public describeSearchResult(hovered: ISearchGridDTO) {
        return RADEnvironmentProxy.getInstance().describeSearchResult({ pdoId: hovered.pdoId });
    }
}

ComponentManager.setViewModel('SearchDescriptionViewModel', SearchDescriptionViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');